// import { uniq } from 'es-toolkit'
import { computed, type MaybeRefOrGetter, unref } from 'vue'

export function getClassList(className?: string) {
  return Array.from(new Set(
    (className || '')
      .split(' ')
      .map(x => x.trim())
      .filter(Boolean),
  ))
}

export function simpleMerge(...classNames: Array<string | undefined>) {
  const processedGroups = classNames.map((className) => {
    const classList = getClassList(className)
    const groupMap = new Map<string, string>()

    for (const cls of classList) {
      const key = getUtilityClassKey(cls)
      if (!groupMap.has(key)) {
        groupMap.set(key, cls)
      }
    }

    return Array.from(groupMap.values())
  })

  const allClasses = processedGroups.flat()
  const finalMap = new Map<string, { class: string, index: number }>()

  allClasses.forEach((cls, index) => {
    const key = getUtilityClassKey(cls)
    finalMap.set(key, { class: cls, index })
  })

  return Array.from(finalMap.values())
    .sort((a, b) => a.index - b.index)
    .map(item => item.class)
    .join(' ')
}

function getUtilityClassKey(className: string): string {
  const modifierMatch = className.match(/^([a-z0-9]+:)+(.+)$/)
  if (modifierMatch) {
    const modifiers = modifierMatch[1]
    const utility = modifierMatch[2]
    return modifiers + getUtilityClassKey(utility)
  }

  const utilityPrefixes = [
    'text',
    'bg',
    'border',
    'p',
    'px',
    'py',
    'pt',
    'pr',
    'pb',
    'pl',
    'm',
    'mx',
    'my',
    'mt',
    'mr',
    'mb',
    'ml',
    'w',
    'h',
    'min-w',
    'min-h',
    'max-w',
    'max-h',
    'top',
    'right',
    'bottom',
    'left',
    'inset',
    'z',
    'opacity',
    'shadow',
    'rounded',
    'font',
    'leading',
    'tracking',
    'space-x',
    'space-y',
    'divide-x',
    'divide-y',
    'gap',
    'justify',
    'items',
    'content',
    'self',
    'flex',
    'grid',
    'col',
    'row',
    'order',
    'transform',
    'scale',
    'rotate',
    'translate',
    'skew',
    'origin',
    'transition',
    'duration',
    'ease',
    'delay',
    'animate',
    'cursor',
    'select',
    'resize',
    'list',
    'appearance',
    'outline',
    'ring',
    'fill',
    'stroke',
  ]

  for (const prefix of utilityPrefixes) {
    if (className.startsWith(`${prefix}-`)) {
      return prefix
    }
  }

  const firstHyphenIndex = className.indexOf('-')
  if (firstHyphenIndex !== -1) {
    return className.slice(0, firstHyphenIndex)
  }

  return className
}

export function useUnoMerge(...classNames: Array<MaybeRefOrGetter<string | undefined>>) {
  return computed(() => simpleMerge(...classNames.map(cls => typeof cls === 'function' ? cls() : unref(cls))))
}
